import React from 'react';
import { Globe, Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const quickLinks = [
    { name: 'Home', href: '/', anchor: null, type: 'home' },
    { name: 'Services', href: '/', anchor: 'services', type: 'anchor' },
    { name: 'Technologies', href: '/', anchor: 'technologies', type: 'anchor' },
    { name: 'About', href: '/', anchor: 'about', type: 'anchor' },
    { name: 'Testimonials', href: '/', anchor: 'testimonials', type: 'anchor' },
    { name: 'Careers', href: '/careers', anchor: null, type: 'link' },
    { name: 'Contact', href: '/', anchor: 'contact', type: 'anchor' }
  ];

  const services = [
    'IT Outsourcing',
    'IT Consulting',
    'Software Development',
    'Building In-house SAAS Products',
    'Cloud Engineering'
  ];

  const technologies = [
    'Java Development',
    'Python Development',
    'PHP Development',
    'AI Engineering',
    'Cloud Solutions',
    'SAP & Oracle'
  ];

  const scrollToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, item) => {
    e.preventDefault();
    
    if (item.type === 'home') {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (item.type === 'link') {
      navigate(item.href);
    } else if (item.type === 'anchor') {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(item.anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(item.anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const openCookieSettings = () => {
    try {
      window.dispatchEvent(new CustomEvent('tripod:open-cookie-settings'));
    } catch (e) {
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex flex-col items-start mb-6">
                <div className="bg-white rounded-md p-1 mb-3">
                  <img 
                    src="/logo.png" 
                    alt="Tripod Technologies Logo" 
                    className="h-12 w-auto"
                  />
                </div>
                <h3 className="text-white text-lg font-semibold">
                  Tripod Technologies Private Limited
                </h3>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Leading IT outsourcing and consulting company delivering innovative solutions globally. 
                Empowering businesses with cutting-edge technology.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">Nagpur, Maharashtra, India</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-400">
                  <Phone className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">+91-89832 30676</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-400">
                  <Mail className="w-4 h-4 text-purple-500" />
                  <span className="text-sm">Info@Tripodtechnologies.com</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link)}
                      className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Our Services</h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service}>
                    <span className="text-gray-400 text-sm">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Technologies */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Technologies</h3>
              <ul className="space-y-3">
                {technologies.map((tech) => (
                  <li key={tech}>
                    <span className="text-gray-400 text-sm">{tech}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Tripod Technologies Private Limited. All rights reserved.
              </p>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4 text-sm">
                <Link
                  to="/privacy"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Cookie settings
                </button>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Globe className="w-4 h-4 text-purple-500" />
                <span>Serving Globally</span>
              </div>
              <button
                onClick={scrollToTop}
                className="bg-purple-600 hover:bg-purple-700 text-white p-3 min-w-[44px] min-h-[44px] rounded-lg transition-colors duration-200"
                aria-label="Scroll to top"
                title="Scroll to top"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
